/**
 * PermissionsSection — Collapsible permissions matrix
 *
 * Displays subjects grouped, with checkboxes for each action (read/create/update/delete).
 * Fetches subjects and actions from the server registry.
 * Supports search filtering from parent searchQuery.
 */

import { useMemo, useState, useEffect } from "react";
import { Grid, Checkbox } from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";

import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import CollapsibleSection from "templates/FullPageDetailTemplate/CollapsibleSection";
import { useMaterialUIController } from "context";
import { useT } from "context/I18nContext";
import useApi from "hooks/useApi";
import { endpoints } from "config/api";

function PermissionsSection({
  roleData,
  updateField,
  expanded,
  onToggle,
  canEdit,
  searchQuery,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const t = useT();
  const { get } = useApi();

  // Fetch subjects and actions from server registry
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [availableActions, setAvailableActions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    get(endpoints.registry.base).then((data) => {
      if (cancelled) return;
      const reg = data.registry || data;
      // Filter out "all" — it's a meta-subject, not a permission target
      const subjects = (reg.subjects || [])
        .map((s) => (typeof s === "string" ? s : s.id))
        .filter((s) => s !== "all");
      // Filter out "manage" — it's the "ALL" column
      const actions = (reg.actions || [])
        .map((a) => (typeof a === "string" ? a : a.id))
        .filter((a) => a !== "manage");
      setAvailableSubjects(subjects);
      setAvailableActions(actions);
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const permissions = roleData?.permissions || [];

  // Filter subjects by search
  const filteredSubjects = useMemo(() => {
    if (!searchQuery?.trim()) return availableSubjects;
    const q = searchQuery.toLowerCase();
    return availableSubjects.filter((s) => s.toLowerCase().includes(q));
  }, [searchQuery, availableSubjects]);

  const togglePermission = (key) => {
    if (permissions.includes(key)) {
      updateField("permissions", permissions.filter((p) => p !== key));
    } else {
      updateField("permissions", [...permissions, key]);
    }
  };

  // Toggle all actions for a subject
  const toggleAllForSubject = (subject) => {
    const subjectKeys = availableActions.map((a) => `${a}:${subject}`);
    const allChecked = subjectKeys.every((k) => permissions.includes(k));
    if (allChecked) {
      updateField("permissions", permissions.filter((p) => !subjectKeys.includes(p)));
    } else {
      const newPerms = [...permissions];
      subjectKeys.forEach((k) => {
        if (!newPerms.includes(k)) newPerms.push(k);
      });
      updateField("permissions", newPerms);
    }
  };

  const labelSx = {
    color: darkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
    fontSize: "0.7rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  return (
    <CollapsibleSection
      title={t("roles.permissions")}
      icon={<SecurityIcon sx={{ color: "white", fontSize: 20 }} />}
      iconGradient={["#8B5CF6", "#C4B5FD"]}
      badge={permissions.length}
      expanded={expanded}
      onToggle={onToggle}
    >
      <KDTypography variant="body2" color="text" mb={2} sx={{ opacity: 0.7 }}>
        {t("roles.permissionsConfig")}
      </KDTypography>

      {/* Header row */}
      <Grid container spacing={1} mb={1}>
        <Grid item xs={3}>
          <KDTypography sx={labelSx}>{t("roles.detail.subject")}</KDTypography>
        </Grid>
        {availableActions.map((action) => (
          <Grid item xs key={action} sx={{ textAlign: "center" }}>
            <KDTypography sx={labelSx}>{action}</KDTypography>
          </Grid>
        ))}
        <Grid item xs={1} sx={{ textAlign: "center" }}>
          <KDTypography sx={labelSx}>{t("common.all")}</KDTypography>
        </Grid>
      </Grid>

      {/* Subject rows */}
      {filteredSubjects.map((subject) => {
        const subjectKeys = availableActions.map((a) => `${a}:${subject}`);
        const allChecked = subjectKeys.every((k) => permissions.includes(k));
        const someChecked = subjectKeys.some((k) => permissions.includes(k));

        return (
          <KDBox
            key={subject}
            sx={{
              py: 1.5,
              px: 1,
              borderBottom: darkMode
                ? "1px solid rgba(255,255,255,0.06)"
                : "1px solid rgba(0,0,0,0.06)",
              "&:hover": {
                backgroundColor: darkMode
                  ? "rgba(255,255,255,0.02)"
                  : "rgba(0,0,0,0.01)",
              },
              borderRadius: "8px",
            }}
          >
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={3}>
                <KDTypography
                  variant="subtitle2"
                  fontWeight="bold"
                  color={darkMode ? "white" : "dark"}
                >
                  {subject}
                </KDTypography>
              </Grid>
              {availableActions.map((action) => {
                const key = `${action}:${subject}`;
                return (
                  <Grid item xs key={key} sx={{ textAlign: "center" }}>
                    <Checkbox
                      checked={permissions.includes(key)}
                      onChange={() => togglePermission(key)}
                      disabled={!canEdit}
                      size="small"
                      sx={darkMode ? {
                        color: "rgba(255,255,255,0.3)",
                        "&.Mui-checked": { color: "#8B5CF6" },
                      } : {
                        "&.Mui-checked": { color: "#8B5CF6" },
                      }}
                    />
                  </Grid>
                );
              })}
              <Grid item xs={1} sx={{ textAlign: "center" }}>
                <Checkbox
                  checked={allChecked}
                  indeterminate={someChecked && !allChecked}
                  onChange={() => toggleAllForSubject(subject)}
                  disabled={!canEdit}
                  size="small"
                  sx={darkMode ? {
                    color: "rgba(255,255,255,0.3)",
                    "&.Mui-checked": { color: "#0D9488" },
                    "&.MuiCheckbox-indeterminate": { color: "#0D9488" },
                  } : {
                    "&.Mui-checked": { color: "#0D9488" },
                    "&.MuiCheckbox-indeterminate": { color: "#0D9488" },
                  }}
                />
              </Grid>
            </Grid>
          </KDBox>
        );
      })}

      {filteredSubjects.length === 0 && (
        <KDBox py={3} textAlign="center">
          <KDTypography variant="body2" color="text" sx={{ opacity: 0.5 }}>
            {t("roles.detail.noMatchingSubjects")}
          </KDTypography>
        </KDBox>
      )}
    </CollapsibleSection>
  );
}

export default PermissionsSection;
